import { useState, useEffect, useRef, type KeyboardEvent, type ChangeEvent, type ComponentPropsWithoutRef } from 'react';
import { Send, Plus, Paperclip, X, Square } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { sendChatMessage, fetchHistory, extractFileText } from '../services/api';
import type { Agent, UserHeaders } from '../services/api';
import type { EmailContext } from '../hooks/useEmailContext';
import { buildEmailAttachmentsFiles } from '../utils/emailAttachments';
import { WorkstationPicker } from './WorkstationPicker';

interface LoadRequest {
  sessionId: string;
  agentId: string;
  nonce: number;
}

interface Props {
  token: string;
  user: UserHeaders;
  agents: Agent[];
  loadRequest?: LoadRequest | null;
  email?: EmailContext | null;
}

// Mismo formato que buildPrompt() usa para la pestaña "Asistente" — un mismo correo se ve
// igual sin importar por qué pestaña se procesó. Se antepone solo al primer mensaje de una
// conversación nueva: el historial que el backend recupera por session_id ya lo conserva para
// los turnos siguientes, así que repetirlo en cada mensaje solo infla el prompt sin necesidad.
const buildEmailContextBlock = (email: EmailContext): string =>
  [
    '[CONTEXTO DEL CORREO ACTUALMENTE ABIERTO]',
    `**Asunto:** ${email.subject}`,
    `**Remitente:** ${email.sender}${email.senderEmail ? ` <${email.senderEmail}>` : ''}`,
    '**Contenido:**',
    email.body,
    '[FIN CONTEXTO DEL CORREO]',
    '',
  ].join('\n');

interface AttachedFileMeta {
  name: string;
  size: number;
}

interface ChatBubble {
  role: 'user' | 'assistant';
  text: string;
  files?: AttachedFileMeta[];
}

const generateSessionId = () =>
  'outlook-chat-' + Math.random().toString(36).substring(2, 15) + '-' + Math.random().toString(36).substring(2, 15);

// Los links del agente (ej. descarga de reporte) deben abrir en una pestaña nueva del
// navegador — el task pane es un iframe angosto, navegar ahí adentro rompe la sesión.
const MARKDOWN_COMPONENTS = {
  a: ({ children, ...props }: ComponentPropsWithoutRef<'a'>) => (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const MAX_FILE_BYTES = 20 * 1024 * 1024; // matches el límite del backend en /files/extract
const PLAIN_TEXT_RE = /\.(txt|md|csv|json|xml|html?|js|py|ts)$/i;
const EXTRACTABLE_RE = /\.(pdf|docx?|xlsx?)$/i;
const IMAGE_RE = /\.(png|jpe?g)$/i;

export function ChatTab({ token, user, agents, loadRequest, email }: Props) {
  const [selectedAgent, setSelectedAgent] = useState('');
  const [sessionId, setSessionId] = useState(generateSessionId);
  const [messages, setMessages] = useState<ChatBubble[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [fileWarning, setFileWarning] = useState('');
  const [isExtractingFiles, setIsExtractingFiles] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const currentAgent = agents.find(a => a.id === selectedAgent);
  const allowFileUpload = currentAgent?.allow_file_upload !== false;

  // Preselecciona el primer agente disponible apenas carga la lista.
  useEffect(() => {
    if (!selectedAgent && agents.length > 0) setSelectedAgent(agents[0].id);
  }, [agents, selectedAgent]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reconstruye un hilo completo cuando el usuario abre una sesión desde Historial
  // (nonce cambia en cada clic, incluso re-clic sobre la misma sesión, para forzar el efecto).
  useEffect(() => {
    if (!loadRequest) return;
    let cancelled = false;
    setSelectedAgent(loadRequest.agentId);
    setSessionId(loadRequest.sessionId);
    setInput('');
    setError('');
    setStatus('');
    setAttachedFiles([]);
    setFileWarning('');
    setSending(true);
    fetchHistory(token, user, loadRequest.sessionId)
      .then(hist => {
        if (cancelled) return;
        setMessages(hist.map(m => ({ role: m.sender === 'user' ? 'user' : 'assistant', text: m.text })));
      })
      .catch(() => {
        if (!cancelled) setError('No se pudo cargar la conversación seleccionada');
      })
      .finally(() => {
        if (!cancelled) setSending(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRequest]);

  const startNewConversation = () => {
    abortControllerRef.current?.abort();
    setSessionId(generateSessionId());
    setMessages([]);
    setInput('');
    setError('');
    setStatus('');
    setAttachedFiles([]);
    setFileWarning('');
  };

  const onFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-seleccionar el mismo archivo despues de removerlo
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setFileWarning(
        `El archivo '${file.name}' (${(file.size / 1024 / 1024).toFixed(1)} MB) supera el límite de 20 MB.`,
      );
      return;
    }
    setFileWarning('');
    setAttachedFiles(prev => [...prev, file]);
  };

  const removeAttachedFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Convierte los adjuntos en bloques de texto embebidos en el mensaje que ve el
  // modelo (el backend no soporta adjuntos multimodales) — mismo formato que
  // desarrolla-front, para que un mismo agente responda igual sin importar el cliente.
  const buildFileContext = async (files: File[]): Promise<string> => {
    let context = '';
    for (const file of files) {
      if (PLAIN_TEXT_RE.test(file.name) && file.size < 5 * 1024 * 1024) {
        try {
          const content = await file.text();
          context += `\n--- CONTENIDO DEL ARCHIVO ADJUNTO: ${file.name} ---\n${content}\n--- FIN ARCHIVO ---\n`;
        } catch {
          context += `\n[Archivo adjunto: ${file.name} (${(file.size / 1024).toFixed(1)} KB) — no se pudo leer su contenido]\n`;
        }
      } else if (EXTRACTABLE_RE.test(file.name)) {
        try {
          const result = await extractFileText(token, user, file);
          context += `\n--- CONTENIDO DEL ARCHIVO ADJUNTO: ${result.filename} ---\n${result.extracted_text}\n--- FIN ARCHIVO ---\n`;
        } catch {
          context += `\n[Archivo adjunto: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB) — no se pudo extraer su contenido en el servidor]\n`;
        }
      } else if (IMAGE_RE.test(file.name)) {
        context += `\n[Imagen adjunta: ${file.name} (${(file.size / 1024).toFixed(1)} KB) — el modelo actual no admite análisis de imágenes; este archivo no fue procesado]\n`;
      } else {
        context += `\n[Archivo adjunto: ${file.name} (${(file.size / 1024).toFixed(1)} KB) — formato no soportado para extracción de contenido]\n`;
      }
    }
    return context;
  };

  const send = async () => {
    const typedText = input.trim();
    if ((!typedText && attachedFiles.length === 0) || !selectedAgent || sending) return;

    const isFirstMessage = messages.length === 0;
    const filesToSend = attachedFiles;
    const fileMeta: AttachedFileMeta[] = filesToSend.map(f => ({ name: f.name, size: f.size }));

    setMessages(prev => [...prev, { role: 'user', text: typedText, files: fileMeta }, { role: 'assistant', text: '' }]);
    setInput('');
    setAttachedFiles([]);
    setFileWarning('');
    setSending(true);
    setStatus('');
    setError('');

    let fullText = typedText;
    if (filesToSend.length > 0) {
      setIsExtractingFiles(true);
      const fileContext = await buildFileContext(filesToSend);
      setIsExtractingFiles(false);
      fullText = `${typedText}\n${fileContext}`.trim();
    }
    if (isFirstMessage && email) {
      let emailBlock = buildEmailContextBlock(email);
      // Adjuntos nativos del correo abierto (p.ej. un memo en PDF) — se leen una sola vez,
      // igual que el resto del contexto del correo, y se procesan con el mismo pipeline de
      // extracción que usa el clip de subida manual (buildFileContext -> extractFileText).
      if (email.attachments.length > 0) {
        setIsExtractingFiles(true);
        const emailFiles = await buildEmailAttachmentsFiles(email.attachments);
        const emailAttachmentsContext = await buildFileContext(emailFiles);
        setIsExtractingFiles(false);
        emailBlock += `[ADJUNTOS DEL CORREO ACTUALMENTE ABIERTO]\n${emailAttachmentsContext}\n[FIN ADJUNTOS DEL CORREO]\n`;
      }
      fullText = `${emailBlock}\n${fullText}`.trim();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      await sendChatMessage(
        token,
        user,
        selectedAgent,
        fullText,
        sessionId,
        // Cada evento "thinking" trae solo el fragmento nuevo (no el acumulado) — reemplazar
        // con setStatus(chunk) directo hacía que el status se viera saltar/perder texto a
        // mitad de una idea. Se acumula para que se lea completo mientras procesa.
        chunk => setStatus(prev => prev + chunk),
        chunk => {
          setMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            next[next.length - 1] = { ...last, text: last.text + chunk };
            return next;
          });
        },
        controller.signal,
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, text: (last.text ? last.text + '\n\n' : '') + '[Cancelado]' };
          return next;
        });
      } else {
        setMessages(prev => prev.slice(0, -1));
        setError(err instanceof Error ? err.message : 'Error inesperado');
      }
    } finally {
      abortControllerRef.current = null;
      setSending(false);
      setStatus('');
    }
  };

  const cancelSend = () => {
    abortControllerRef.current?.abort();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const hasThread = messages.length > 0;

  return (
    <div className="chat-tab">
      <div className="btn-new-chat-container">
        <button className="btn-ghost btn-outline" onClick={startNewConversation} title="Nueva conversación">
          <Plus size={13} /> Nueva conversación
        </button>
      </div>

      <WorkstationPicker agents={agents} selected={selectedAgent} onChange={setSelectedAgent} disabled={sending || hasThread} />
      {hasThread && (
        <p className="field-hint">Para hablar con otro agente, inicia una nueva conversación.</p>
      )}

      {error && <p className="error-text">{error}</p>}

      <div className="chat-thread">
        {messages.length === 0 ? (
          <p className="muted">
            {email
              ? `Puedes preguntar sobre el correo abierto ("${email.subject}") o conversar libremente con el agente seleccionado.`
              : 'Escribe un mensaje para conversar libremente con el agente seleccionado.'}
          </p>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`chat-bubble chat-bubble-${m.role}`}>
              {m.files && m.files.length > 0 && (
                <div className="chat-bubble-files">
                  {m.files.map((f, fi) => (
                    <span key={fi} className="attached-chip">
                      <Paperclip size={11} /> {f.name}
                    </span>
                  ))}
                </div>
              )}
              <div className="chat-bubble-text">
                {m.text ? (
                  <ReactMarkdown components={MARKDOWN_COMPONENTS}>{m.text}</ReactMarkdown>
                ) : sending && i === messages.length - 1 ? (
                  <div className="chat-thinking">
                    {/* Envuelto en un único span: si el markdown del status trae varios
                        párrafos (uno por cada anuncio 💭 de una iteración distinta), cada
                        <p> sería hijo DIRECTO de .chat-thinking (que es un contenedor flex).
                        CSS "blockifica" todo hijo directo de un flex item — eso anulaba el
                        display:inline de .chat-thinking p más abajo y cada párrafo terminaba
                        como su propia caja suelta en una grilla, en vez de fluir como texto
                        continuo. Con el wrapper, el único hijo directo del flex container es
                        este span (que sí se blockifica, correcto: ocupa una fila junto a los
                        puntos), y los <p> internos quedan como descendientes normales donde
                        el display:inline sí aplica. */}
                    <span className="chat-thinking-text">
                      {status ? <ReactMarkdown components={MARKDOWN_COMPONENTS}>{status}</ReactMarkdown> : 'Pensando'}
                    </span>
                    <span className="chat-thinking-dots"><span>.</span><span>.</span><span>.</span></span>
                  </div>
                ) : ''}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {isExtractingFiles && <p className="muted chat-status">Leyendo contenido del archivo adjunto…</p>}

      {attachedFiles.length > 0 && (
        <div className="attached-files-preview">
          {attachedFiles.map((f, i) => (
            <span key={i} className="attached-file-chip">
              <Paperclip size={12} />
              <span className="file-name">{f.name}</span>
              <span className="file-size">({(f.size / 1024 / 1024).toFixed(1)} MB)</span>
              <button type="button" className="btn-remove-file" onClick={() => removeAttachedFile(i)} title="Quitar adjunto">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {fileWarning && (
        <p className="error-text">
          {fileWarning}
          <button type="button" className="btn-ghost" style={{ float: 'right', padding: 0 }} onClick={() => setFileWarning('')}>
            <X size={12} />
          </button>
        </p>
      )}

      <div className="chat-input-row">
        {allowFileUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={onFileSelected}
              accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.json,.png,.jpg,.jpeg"
            />
            <button
              type="button"
              className="btn-icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || !selectedAgent}
              title="Adjuntar archivo (máx. 20 MB)"
            >
              <Paperclip size={16} />
            </button>
          </>
        )}
        <textarea
          className="textarea"
          rows={2}
          placeholder="Escribe tu mensaje… (Enter para enviar, Shift+Enter para salto de línea)"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || !selectedAgent}
        />
        {sending ? (
          <button className="btn-primary btn-icon-send" onClick={cancelSend} title="Cancelar">
            <Square size={16} />
          </button>
        ) : (
          <button
            className="btn-primary btn-icon-send"
            onClick={send}
            disabled={(!input.trim() && attachedFiles.length === 0) || !selectedAgent}
            title="Enviar"
          >
            <Send size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
